import { useParams, Link } from "react-router-dom";
import { useOrder } from "@/pages/order/hooks/useOrders";
import { Button } from "@groot/ui/button";
import { EmptyState } from "@groot/ui/empty-state";
import { LoadingState } from "@groot/ui/loading-spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { cn } from "@groot/ui/lib/utils";
import { ArrowLeft, Printer, Download, SearchX } from "lucide-react";
import { formatDisplayDate } from "@groot/shell/lib/date.utils";

const BRAND_COLOR = "#2c3667";

// The invoice is a printed sheet: it stays white with fixed ink colors in
// both themes, so it overrides the table primitives' theme tokens.
const INVOICE_HEAD = "py-2 text-sm font-semibold normal-case tracking-normal text-[#2c3667]";
const INVOICE_ROW = "border-b border-[#f3f4f6] hover:bg-transparent";
const INVOICE_LABEL_CELL = "py-2 pr-4 text-right text-[#6b7280]";

export function OrderInvoice() {
  const { id } = useParams<{ id: string }>();
  const orderId = parseInt(id || "0", 10);
  const { data: order, isLoading } = useOrder(orderId);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <LoadingState text="Loading invoice…" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <EmptyState
          icon={SearchX}
          title="Order not found"
          description="This order may have been deleted or the link is invalid."
          action={
            <Button variant="outline" asChild>
              <Link to="/orders">
                <ArrowLeft className="size-4" />
                Back to Orders
              </Link>
            </Button>
          }
        />
      </div>
    );
  }

  const subtotal = order.items?.reduce((sum, item) => sum + Number(item.lineTotal), 0) ?? 0;
  const discountAmount = Number(order.discountAmount ?? 0);
  const discountType = order.discountType;
  const discountValue = Number(order.discountValue ?? 0);
  const shippingCost = Number(order.shippingCost ?? 0);
  const total = Number(order.total ?? 0);

  const paymentStatusLabel = order.paymentStatus === "paid" ? "Paid" : "Not Paid";

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-muted print:bg-white">
      {/* Toolbar — hidden on print */}
      <div className="print:hidden sticky top-0 z-10 bg-background/80 backdrop-blur border-b border-border">
        <div className="max-w-[210mm] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/orders/${order.id}`}>
              <ArrowLeft className="size-4" />
              <span className="sm:hidden">Back</span>
              <span className="hidden sm:inline">Back to Order</span>
            </Link>
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Download className="size-4" />
              Save PDF
            </Button>
            <Button size="sm" onClick={handlePrint}>
              <Printer className="size-4" />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Invoice Page */}
      <div className="max-w-[210mm] mx-auto bg-white shadow-lg print:shadow-none print:max-w-none min-h-[calc(100vh-48px)] print:min-h-screen flex flex-col">
        <div className="p-5 sm:p-12 print:p-6 flex flex-col flex-grow">
          {/* Header: Logo + Invoice Title */}
          <div className="flex items-start justify-between mb-8 print:mb-6">
            <div>
              <img src="/logo.png" alt="Riqah Design" className="h-14 w-auto" />
            </div>
            <h1
              className="text-2xl sm:text-3xl print:text-2xl"
              style={{ color: BRAND_COLOR, fontWeight: 600 }}
            >
              Invoice
            </h1>
          </div>

          {/* From & Details */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-12 sm:gap-8 print:grid-cols-12 print:gap-8 mb-6 print:mb-5">
            {/* From */}
            <div className="sm:col-span-7 print:col-span-7">
              <p className="text-sm font-semibold mb-1.5" style={{ color: BRAND_COLOR }}>
                From
              </p>
              <ul className="space-y-0.5 text-sm text-[#323334]">
                <li className="font-semibold">Riqah Design</li>
                <li>7/7 Balusamy Street, Palavanthangal</li>
                <li>Chennai 600114, Tamil Nadu, India</li>
                <li>
                  <a href="https://riqahdesign.com" style={{ color: "#323334" }}>
                    https://riqahdesign.com
                  </a>
                </li>
              </ul>
            </div>

            {/* Details */}
            <div className="sm:col-span-5 print:col-span-5">
              <p className="text-sm font-semibold mb-1.5" style={{ color: BRAND_COLOR }}>
                Details
              </p>
              <div className="grid grid-cols-2 gap-x-4 text-sm text-[#323334]">
                <div className="space-y-0.5">
                  <p>Invoice Number:</p>
                  <p>Date of Issue:</p>
                  <p>Status:</p>
                </div>
                <div className="space-y-0.5 font-medium">
                  <p>{order.orderNumber}</p>
                  <p>{formatDisplayDate(order.orderDate)}</p>
                  <p>
                    <span
                      className="inline-block px-2 py-0.5 rounded text-xs font-semibold"
                      style={{
                        backgroundColor: order.paymentStatus === "paid" ? "#dcfce7" : "#fef3c7",
                        color: order.paymentStatus === "paid" ? "#166534" : "#92400e",
                      }}
                    >
                      {paymentStatusLabel}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* To */}
          <div className="mb-6 print:mb-5">
            <p className="text-sm font-semibold mb-1.5" style={{ color: BRAND_COLOR }}>
              To
            </p>
            <ul className="space-y-0.5 text-sm text-[#323334] leading-snug">
              <li className="font-semibold">{order.customer?.name}</li>
              {order.customer?.address &&
                order.customer.address.split("\n").map((line, i) => <li key={i}>{line}</li>)}
              {order.customer?.mobileNumber && <li>{order.customer.mobileNumber}</li>}
            </ul>
          </div>

          {/* Items — phones (screen only): stacked list */}
          <div className="mt-6 sm:hidden print:hidden text-sm text-[#323334]">
            <p className="font-semibold mb-1.5" style={{ color: BRAND_COLOR }}>
              Items
            </p>
            <ul className="divide-y divide-[#e5e7eb] border-y border-[#e5e7eb]">
              {order.items?.map((item) => (
                <li key={item.id} className="flex items-start justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div>
                      {item.productName}
                      {item.size && <span className="ml-1 text-[#9ca3af]">({item.size})</span>}
                    </div>
                    <div className="text-xs text-[#9ca3af]">
                      {item.quantity} × ₹{Number(item.unitPrice).toLocaleString("en-IN")}
                    </div>
                  </div>
                  <span className="shrink-0">
                    ₹{Number(item.lineTotal).toLocaleString("en-IN")}
                  </span>
                </li>
              ))}
            </ul>
            <dl className="mt-2 space-y-1">
              {(discountAmount > 0 || shippingCost > 0) && (
                <div className="flex justify-between">
                  <dt className="text-[#4a4a4a]">Subtotal</dt>
                  <dd>₹{subtotal.toLocaleString("en-IN")}</dd>
                </div>
              )}
              {discountAmount > 0 && (
                <div className="flex justify-between">
                  <dt className="text-[#4a4a4a]">
                    Discount{discountType === "percentage" && ` (${discountValue}%)`}
                  </dt>
                  <dd className="text-[#ef4444]">−₹{discountAmount.toLocaleString("en-IN")}</dd>
                </div>
              )}
              {shippingCost > 0 && (
                <div className="flex justify-between">
                  <dt className="text-[#4a4a4a]">Shipping</dt>
                  <dd>₹{shippingCost.toLocaleString("en-IN")}</dd>
                </div>
              )}
              <div className="flex justify-between pt-1 text-base font-semibold">
                <dt className="font-medium text-[#4a4a4a]">Total</dt>
                <dd>₹{total.toLocaleString("en-IN")}</dd>
              </div>
            </dl>
          </div>

          {/* Items Table — tablets, desktop and print */}
          <div className="mt-6 print:mt-5 hidden sm:block print:block">
            <Table className="text-[#323334]">
              <TableHeader>
                <TableRow className="border-b-2 border-[#e5e7eb] hover:bg-transparent">
                  <TableHead className={cn(INVOICE_HEAD, "min-w-[400px] px-0")}>
                    Description
                  </TableHead>
                  <TableHead className={cn(INVOICE_HEAD, "text-right")}>Units</TableHead>
                  <TableHead className={cn(INVOICE_HEAD, "text-right")}>Unit Price</TableHead>
                  <TableHead className={cn(INVOICE_HEAD, "px-0 text-right")}>
                    Amount (INR)
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="[&_tr:last-child]:border-0">
                {order.items?.map((item) => (
                  <TableRow key={item.id} className={INVOICE_ROW}>
                    <TableCell className="px-0 py-2 whitespace-normal">
                      {item.productName}
                      {item.size && <span className="ml-1 text-[#9ca3af]">({item.size})</span>}
                    </TableCell>
                    <TableCell className="py-2 text-right">{item.quantity}</TableCell>
                    <TableCell className="py-2 text-right">
                      ₹{Number(item.unitPrice).toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell className="px-0 py-2 text-right">
                      ₹{Number(item.lineTotal).toLocaleString("en-IN")}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Subtotal */}
                {discountAmount > 0 || shippingCost > 0 ? (
                  <TableRow className={INVOICE_ROW}>
                    <TableCell colSpan={3} className={INVOICE_LABEL_CELL}>
                      Subtotal
                    </TableCell>
                    <TableCell className="px-0 py-2 text-right">
                      ₹{subtotal.toLocaleString("en-IN")}
                    </TableCell>
                  </TableRow>
                ) : null}

                {/* Discount */}
                {discountAmount > 0 && (
                  <TableRow className={INVOICE_ROW}>
                    <TableCell colSpan={3} className={INVOICE_LABEL_CELL}>
                      Discount
                      {discountType === "percentage" && ` (${discountValue}%)`}
                    </TableCell>
                    <TableCell className="px-0 py-2 text-right text-[#ef4444]">
                      −₹{discountAmount.toLocaleString("en-IN")}
                    </TableCell>
                  </TableRow>
                )}

                {/* Shipping */}
                {shippingCost > 0 && (
                  <TableRow className={INVOICE_ROW}>
                    <TableCell colSpan={3} className={INVOICE_LABEL_CELL}>
                      Shipping
                    </TableCell>
                    <TableCell className="px-0 py-2 text-right">
                      ₹{shippingCost.toLocaleString("en-IN")}
                    </TableCell>
                  </TableRow>
                )}

                {/* Total */}
                <TableRow className="border-0 hover:bg-transparent">
                  <TableCell
                    colSpan={3}
                    className="py-2 pr-4 text-right font-medium text-[#4a4a4a]"
                  >
                    Total
                  </TableCell>
                  <TableCell className="px-0 py-2 text-right text-base font-semibold">
                    ₹{total.toLocaleString("en-IN")}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* Footer */}
          <div className="mt-auto pt-8 print:pt-6 text-center">
            <p className="text-base text-[#323334] font-medium">
              Thank you for doing business with us!
            </p>
            <p className="text-sm text-[#A6ACB3] mt-2">
              We appreciate your trust in Riqah Design and hope to work with you again.
            </p>
            <div className="mt-3 flex items-center justify-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={BRAND_COLOR}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
              <span className="text-sm" style={{ color: BRAND_COLOR }}>
                instagram.com/riqahdesign
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
